/**
 * A message you have sent must never disappear.
 *
 * `docs/message-outbox-plan.md`, Phase 1. Today a send that cannot reach the
 * server is dropped: the bubble is removed in a bare `catch`, and on the 1:1
 * screen the typed text goes with it. Filmed four times on build 12 — at 20ms
 * the bubble vanishes before it paints at all, and at 2.1s the sender watches
 * it go.
 *
 * This module is the RULES half, deliberately **zero imports**, so the root
 * `node --test` suites can drive it — the same split as `retry-send.ts` and
 * `chat-follow.ts`. The wiring, the timers and the API calls live in
 * `state/outbox-store.ts`.
 *
 * Three properties this has to hold, each of them a decision already taken:
 *
 *  1. **Nothing accepted is ever destroyed.** A full queue REFUSES a new
 *     message rather than evicting an old one — eviction is bounded in the
 *     technical sense and is exactly the defect this exists to remove.
 *  2. **There is no retry ceiling.** A message that keeps failing transiently
 *     stays pending until it sends. A ceiling fires precisely when the network
 *     is bad rather than when the conversation is closed, so the conversation's
 *     own state could never explain the loss.
 *  3. **A pending message is never shown as failed.** `queued` and `sending`
 *     are tracked apart because the flush loop needs to know what is in
 *     flight, but they render identically — the owner's call, and the WhatsApp
 *     behaviour this is modelled on. There is no `failed` state to reach.
 */

export type OutboxThreadKind = "chat" | "group";

/**
 * One message waiting to be sent.
 *
 * `clientMessageId` is minted ONCE, at enqueue, and travels on every attempt.
 * That is the whole safety argument: the server's permanent partial unique
 * index on `(chat_id, sender_id, client_message_id)` turns any later attempt
 * into a lookup of the message already stored. `sendWithRetry` mints its key
 * *inside* the call, which is correct for one attempt and catastrophic for a
 * queue — a second flush would mint a second key and post the message twice.
 *
 * `createdAt` is a real ISO timestamp, never the empty string the group
 * screen's optimistic message used to carry: the inbox needs a value to place
 * this row by, and the thread needs one to sort by.
 */
export type OutboxEntry = {
  localId: string;
  threadKind: OutboxThreadKind;
  threadId: string;
  clientMessageId: string;
  content: string;
  replyTargetId?: string;
  createdAt: string;
  /** Two live states, one appearance. See the header. */
  status: "queued" | "sending";
  attempts: number;
  /**
   * Whether this send has taken long enough to be worth telling the sender
   * about. NOT a third status — `queued` and `sending` still render
   * identically, and there is still no failed appearance. This governs one
   * thing: whether the bubble shows its waiting marker at all.
   *
   * Build 13 showed the marker on every send, including the ones that landed
   * in 120-170ms, so a normal message flashed a clock it had no reason to.
   * The owner's call: the marker reports a problem, it does not narrate the
   * happy path.
   */
  slow: boolean;
};

/**
 * Bounds, per the repository rule that nothing grows without a limit.
 *
 * Per-thread as well as overall, because one runaway conversation must not be
 * able to fill the queue for every other one.
 */
export const MAX_ENTRIES_PER_THREAD = 50;
export const MAX_ENTRIES_TOTAL = 200;

/**
 * How long a send may take before its bubble admits it is waiting.
 *
 * Measured on build 13 (SM-G998B): sends that reached the server landed in
 * 120-170ms, and the retry path — the case the marker exists for — takes at
 * least `RETRY_BASE_MS` before it could possibly succeed. 600ms sits clear of
 * the first and well under the second, so a healthy send never shows a marker
 * and a send that is actually in trouble always does.
 */
export const SLOW_SEND_AFTER_MS = 600;

/** First retry waits this long; each subsequent one doubles, up to the cap. */
export const RETRY_BASE_MS = 1_000;
export const RETRY_MAX_MS = 30_000;

export type EnqueueResult =
  | { accepted: true; entries: OutboxEntry[] }
  | { accepted: false; reason: "thread_full" | "outbox_full" };

/**
 * Add a message to the queue, or refuse it.
 *
 * Refusal is the honest failure: the composer keeps the text and says why, and
 * the user can act on it. The alternative — accepting and quietly dropping the
 * oldest — destroys a message somebody typed to satisfy a limit they cannot
 * see, which is the defect this plan removes rather than relocates.
 */
export function enqueue(
  entries: OutboxEntry[],
  entry: OutboxEntry,
): EnqueueResult {
  if (entries.length >= MAX_ENTRIES_TOTAL) {
    return { accepted: false, reason: "outbox_full" };
  }
  const inThread = entries.filter(
    (e) => e.threadKind === entry.threadKind && e.threadId === entry.threadId,
  ).length;
  if (inThread >= MAX_ENTRIES_PER_THREAD) {
    return { accepted: false, reason: "thread_full" };
  }
  return { accepted: true, entries: [...entries, entry] };
}

/** Everything still pending for one thread, in the order it was written. */
export function entriesForThread(
  entries: OutboxEntry[],
  threadKind: OutboxThreadKind,
  threadId: string,
): OutboxEntry[] {
  return entries.filter(
    (e) => e.threadKind === threadKind && e.threadId === threadId,
  );
}

/**
 * The pending entries a thread should actually DRAW, given what it already has.
 *
 * Exactly one row per message, at every instant, whatever order things arrive
 * in. That is the whole contract, and it is not obvious, because a message the
 * outbox is holding can come back three different ways:
 *
 *   - the send response,
 *   - the thread's own socket (the one-to-one room broadcast INCLUDES the
 *     sender — `chat.gateway.ts` emits with `server.to(room)`),
 *   - a catch-up page after a reconnect.
 *
 * The saved row carries the server's id and the pending row carries
 * `outbox-…`, so nothing about the two rows says they are the same message.
 * Without this the thread shows both for a frame — one row too many — and the
 * older code, which removed the entry before announcing the save, showed
 * neither — one row too few. In an inverted list either one lifts every message
 * above it and drops it back: the shake filmed on build 14, worst on reconnect
 * where a whole queue settles at once.
 *
 * `clientMessageId` is the only value both rows share, which is why the server
 * now echoes it. An entry whose key is already on screen is not drawn; the
 * store removes it a moment later and nothing moves when it does.
 */
export function visiblePending(
  entries: OutboxEntry[],
  saved: { clientMessageId?: string | null }[],
): OutboxEntry[] {
  const landed = new Set<string>();
  for (const message of saved) {
    if (message.clientMessageId) landed.add(message.clientMessageId);
  }
  if (landed.size === 0) return entries;
  return entries.filter((entry) => !landed.has(entry.clientMessageId));
}

/**
 * The next entry to attempt for a thread, or null.
 *
 * **Single-flight per thread, in enqueue order.** Two queued messages must not
 * race: the second landing first would reorder the conversation permanently,
 * and unlike a display glitch that is a wrong record on the server. A thread
 * with something already in flight yields nothing until it settles.
 *
 * Threads do not block each other — the filter is per thread, so a stuck
 * conversation cannot hold up the rest.
 */
export function nextToSend(
  entries: OutboxEntry[],
  threadKind: OutboxThreadKind,
  threadId: string,
): OutboxEntry | null {
  const mine = entriesForThread(entries, threadKind, threadId);
  if (mine.some((e) => e.status === "sending")) return null;
  return mine.find((e) => e.status === "queued") ?? null;
}

/** Every thread with something waiting, so the flush loop knows where to look. */
export function threadsWithWork(
  entries: OutboxEntry[],
): { threadKind: OutboxThreadKind; threadId: string }[] {
  const seen = new Set<string>();
  const out: { threadKind: OutboxThreadKind; threadId: string }[] = [];
  for (const e of entries) {
    const key = `${e.threadKind}:${e.threadId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ threadKind: e.threadKind, threadId: e.threadId });
  }
  return out;
}

/** Mark an entry in flight. Its key does not change — see `OutboxEntry`. */
export function beginSending(
  entries: OutboxEntry[],
  localId: string,
): OutboxEntry[] {
  return entries.map((e) =>
    e.localId === localId ? { ...e, status: "sending" as const } : e,
  );
}

/**
 * The send landed: the entry leaves the queue.
 *
 * From here the server's own row is the only record, and its `sentAt` is the
 * only timestamp the inbox will use for this conversation.
 */
export function settle(
  entries: OutboxEntry[],
  localId: string,
): OutboxEntry[] {
  return entries.filter((e) => e.localId !== localId);
}

/**
 * The attempt failed: back to `queued`, attempts incremented, nothing dropped.
 *
 * There is no branch here that removes the entry, and that is deliberate
 * rather than unfinished. Dropping requires proving a message can never be
 * sent, and the API's error body carries no machine-readable code — only a
 * human sentence — so terminal cannot be positively identified today. The
 * governing rule is fail-safe: if it cannot be proved terminal, it stays
 * pending. Silence is never inferred from a status code.
 */
export function failAttempt(
  entries: OutboxEntry[],
  localId: string,
): OutboxEntry[] {
  return entries.map((e) =>
    e.localId === localId
      ? {
          ...e,
          status: "queued" as const,
          attempts: e.attempts + 1,
          // A round trip that failed is slow by definition, whatever the
          // clock says — the next attempt cannot even begin for
          // `RETRY_BASE_MS`. Marking it here means the marker is already up
          // when the timer would have raised it, rather than a moment later.
          slow: true,
        }
      : e,
  );
}

/**
 * The send has been waiting long enough to show its marker.
 *
 * **Returns the same array when nothing changed**, and that is load-bearing
 * rather than a micro-optimisation: the store fires one of these per enqueued
 * message, so the overwhelmingly common case is a timer landing after the
 * message already sent. A fresh array there would re-render both thread
 * screens 600ms after every successful send, for nothing.
 *
 * One-way. Nothing clears `slow`, so a marker that has appeared cannot blink
 * out on a retry and back in again.
 */
export function markSlow(
  entries: OutboxEntry[],
  localId: string,
): OutboxEntry[] {
  const target = entries.find((e) => e.localId === localId);
  if (!target || target.slow) return entries;
  return entries.map((e) => (e.localId === localId ? { ...e, slow: true } : e));
}

/**
 * How long to wait before the next attempt.
 *
 * Exponential with a cap, because offline failures return instantly: an
 * unthrottled retry would spin as fast as the event loop allows and burn
 * battery to learn the same thing repeatedly. The cap keeps a long outage from
 * pushing the next attempt hours away, so reconnecting still recovers quickly.
 */
export function retryDelayMs(attempts: number): number {
  if (attempts <= 0) return 0;
  const raw = RETRY_BASE_MS * 2 ** (attempts - 1);
  return Math.min(raw, RETRY_MAX_MS);
}

/**
 * Where a conversation's row belongs in the inbox while it holds pending
 * messages.
 *
 * The device clock is used for ONE thing: placing this device's own pending
 * rows, for as long as they are pending. `updatedAt` stays server-only —
 * patching it with `Date.now()` would put clock skew into the key every row is
 * compared by, which is the ordering work this must not disturb.
 *
 * Clamped so a queued message can never sort BELOW the message it follows.
 * Returns the row's own `updatedAt` when there is nothing pending, so a caller
 * can use this unconditionally.
 */
export function pendingSortAt(
  serverUpdatedAt: string,
  entries: OutboxEntry[],
): string {
  if (entries.length === 0) return serverUpdatedAt;
  const newest = entries.reduce(
    (max, e) => (e.createdAt > max ? e.createdAt : max),
    entries[0].createdAt,
  );
  return newest > serverUpdatedAt ? newest : serverUpdatedAt;
}
